import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Form, Input, InputNumber, Select, Space, Table, Tag } from "antd"
import { useNavigate } from "react-router-dom"
import { ClusterSummary, MatchClusterStatus } from "../store/types"

const STATUSES: MatchClusterStatus[] = ["FORMING", "VALIDATING", "VALIDATED", "ABANDONED", "REJECTED"]

const STATUS_COLOR: Record<MatchClusterStatus, string> = {
  FORMING: "blue",
  VALIDATING: "gold",
  VALIDATED: "green",
  ABANDONED: "default",
  REJECTED: "red",
}

const DEFAULT_LIMIT = 50

export const ClusterList: FC = observer(() => {
  const { dataStore: { matchClusteringStore } } = useStore()
  const navigate = useNavigate()

  const [filters, setFilters] = useState<{
    gameId?: number
    statuses?: MatchClusterStatus[]
    accountId?: string
  }>({})

  useEffect(() => {
    // Initial fetch — deliberately ignores `filters` (mount-time empty
    // state), so the effect only depends on the store identity. Subsequent
    // filter changes go through Apply / Reset, not this effect.
    matchClusteringStore.loadClusters({ offset: 0, limit: DEFAULT_LIMIT })
  }, [matchClusteringStore])

  const applyFilters = () => {
    matchClusteringStore.loadClusters({ ...filters, offset: 0, limit: DEFAULT_LIMIT })
  }

  const columns = [
    {
      title: "Cluster",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <Button type="link" onClick={() => navigate(`/match-clustering/clusters/${id}`)}>
          {id.slice(0, 8)}…
        </Button>
      ),
    },
    {
      title: "Game",
      dataIndex: "gameId",
      key: "gameId",
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (s: MatchClusterStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: "Members",
      dataIndex: "memberCount",
      key: "memberCount",
      width: 90,
    },
    {
      title: "Freeze",
      dataIndex: "freezeDeadline",
      key: "freezeDeadline",
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: "Validated",
      dataIndex: "validatedAt",
      key: "validatedAt",
      width: 180,
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "Rejected by",
      dataIndex: "rejectedBy",
      key: "rejectedBy",
      width: 140,
      render: (v: string | null) => v ? v.slice(0, 8) + "…" : "—",
    },
  ]

  return (
    <Card title="Match Clusters">
      <Form layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item label="Game ID">
          <InputNumber
            value={filters.gameId}
            onChange={v => setFilters({ ...filters, gameId: v ?? undefined })}
            placeholder="any"
          />
        </Form.Item>
        <Form.Item label="Status">
          <Select
            mode="multiple"
            value={filters.statuses}
            onChange={v => setFilters({ ...filters, statuses: v })}
            options={STATUSES.map(s => ({ value: s, label: s }))}
            style={{ minWidth: 220 }}
            placeholder="any"
            allowClear
          />
        </Form.Item>
        <Form.Item label="Account">
          <Input
            value={filters.accountId}
            onChange={e => setFilters({ ...filters, accountId: e.target.value || undefined })}
            placeholder="UUID"
            style={{ width: 280 }}
            allowClear
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={applyFilters}>Apply</Button>
            <Button
              onClick={() => {
                setFilters({})
                matchClusteringStore.loadClusters({ offset: 0, limit: DEFAULT_LIMIT })
              }}
            >
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <Table<ClusterSummary>
        rowKey="id"
        dataSource={matchClusteringStore.clusters.slice()}
        columns={columns}
        loading={matchClusteringStore.isLoading}
        pagination={{
          total: matchClusteringStore.total,
          pageSize: matchClusteringStore.currentQuery.limit ?? DEFAULT_LIMIT,
          current:
            Math.floor((matchClusteringStore.currentQuery.offset ?? 0) / (matchClusteringStore.currentQuery.limit ?? DEFAULT_LIMIT)) + 1,
          onChange: (page, pageSize) => {
            matchClusteringStore.loadClusters({ ...filters, offset: (page - 1) * pageSize, limit: pageSize })
          },
        }}
      />
    </Card>
  )
})
